/**
 * Assistant operations — retrieve (search-only) and query (with LLM).
 *
 * retrieve: Uses the typed SDK route POST /v1/assistant/retrieve.
 *           Returns matching chunks without LLM generation.
 *
 * queryAssistant: Returns the raw Response for SSE streaming.
 *                 Uses raw fetch because the SDK client doesn't support streaming responses.
 */

import type { ArbiClient, components } from '@arbidocs/client'
import { requireData } from '../errors.js'
import { authenticatedFetch, type AuthHeaders } from '../fetch.js'

type MessageInput = components['schemas']['MessageInput']
type ToolEntry = NonNullable<MessageInput['tools']>[string]

// ── Retrieval tool builder ──────────────────────────────────────────────────

export function buildRetrievalChunkTool(
  docIds: string[],
  searchMode?: 'semantic' | 'keyword' | 'hybrid'
): components['schemas']['RetrievalChunkTool'] {
  return {
    name: 'retrieval_chunk',
    description: 'retrieval chunk',
    tool_args: { doc_ext_ids: docIds, ...(searchMode ? { search_mode: searchMode } : {}) },
    tool_responses: {},
  }
}

export function buildRetrievalFullContextTool(
  docIds: string[]
): components['schemas']['RetrievalFullContextTool'] {
  return {
    name: 'retrieval_full_context',
    description: 'retrieval full context',
    tool_args: { doc_ext_ids: docIds },
    tool_responses: {},
  }
}

export function buildRetrievalTocTool(docIds: string[]): components['schemas']['RetrievalTOCTool'] {
  return {
    name: 'retrieval_toc',
    description: 'retrieval toc',
    tool_args: { doc_ext_ids: docIds },
    tool_responses: {},
  }
}

// ── Retrieve (search-only, uses typed SDK) ──────────────────────────────────

export interface RetrieveOptions {
  arbi: ArbiClient
  workspaceId: string
  query: string
  docIds: string[]
  searchMode?: 'semantic' | 'keyword' | 'hybrid'
  fullContextDocIds?: string[]
  tocDocIds?: string[]
  model?: string
}

export interface ChunkMetadata {
  doc_ext_id?: string | null
  doc_title?: string | null
  chunk_ext_id: string
  chunk_pg_idx: number
  chunk_doc_idx: number
  page_number: number
  score?: number | null
  rerank_score?: number | null
  tokens?: number | null
  created_at: string
  heading: boolean
}

export interface Chunk {
  metadata: ChunkMetadata
  content: string
}

export interface RetrieveResult {
  retrieval_chunk?: {
    tool_responses: Record<string, Chunk[]>
  }
  retrieval_full_context?: {
    tool_responses: Record<string, Chunk[]>
  }
  retrieval_toc?: {
    tool_responses: Record<string, Record<string, unknown>[]>
  }
}

/**
 * Search documents and return matching chunks without LLM generation.
 * Uses the typed SDK route POST /v1/assistant/retrieve.
 */
export async function retrieve(options: RetrieveOptions): Promise<RetrieveResult> {
  const { arbi, workspaceId, query, docIds, searchMode, fullContextDocIds, tocDocIds, model } =
    options

  const tools: Record<string, ToolEntry> = {
    retrieval_chunk: buildRetrievalChunkTool(docIds, searchMode),
  }

  if (fullContextDocIds && fullContextDocIds.length > 0) {
    tools.retrieval_full_context = buildRetrievalFullContextTool(fullContextDocIds)
  }

  if (tocDocIds && tocDocIds.length > 0) {
    tools.retrieval_toc = buildRetrievalTocTool(tocDocIds)
  }

  const body: MessageInput = {
    input: query,
    workspace_ext_id: workspaceId,
    stream: false,
    background: false,
    store: true,
    tools,
    ...(model ? { model } : {}),
  }

  const { data, error } = await arbi.fetch.POST('/v1/assistant/retrieve', { body })

  if (error) {
    // Surface the server's structured `detail` (FastAPI HTTPException payload)
    // when present, otherwise fall back to the raw error. The previous
    // `JSON.stringify(error)` produced `"Internal Server Error"` for plain
    // 500s, which gave the user no actionable signal.
    const detail = (error as { detail?: unknown })?.detail
    let message: string
    if (typeof detail === 'string') {
      message = detail
    } else if (detail && typeof detail === 'object') {
      const d = detail as { hint?: unknown; code?: unknown }
      message = (typeof d.hint === 'string' && d.hint) || JSON.stringify(detail)
    } else {
      message = JSON.stringify(error)
    }
    throw new Error(`Retrieve failed: ${message}`)
  }

  // Response type is `unknown` in schema, cast to our typed result
  return data as RetrieveResult
}

// ── Query (with LLM, raw fetch for SSE streaming) ───────────────────────────

export interface AssistantQueryOptions extends AuthHeaders {
  workspaceId: string
  question: string
  docIds: string[]
  previousResponseId?: string | null
  model?: string
}

/**
 * Send a query to the RAG assistant. Returns the raw Response
 * so the caller can stream or buffer as needed.
 *
 * Uses raw fetch (not the SDK client) because we need the streaming
 * Response body — the SDK client consumes the body to parse JSON.
 */
export async function queryAssistant(options: AssistantQueryOptions): Promise<Response> {
  const { workspaceId, question, docIds, previousResponseId, model, ...auth } = options

  const body: MessageInput = {
    input: question,
    workspace_ext_id: workspaceId,
    stream: true,
    background: false,
    store: true,
    tools: {
      retrieval_chunk: buildRetrievalChunkTool(docIds),
      retrieval_full_context: buildRetrievalFullContextTool([]),
    },
    ...(previousResponseId ? { previous_response_id: previousResponseId } : {}),
    ...(model ? { model } : {}),
  }

  return authenticatedFetch({
    ...auth,
    path: '/v1/assistant/query',
    method: 'POST',
    body: JSON.stringify(body),
    headers: { 'Content-Type': 'application/json' },
  })
}

// ── Respond to agent (human-in-the-loop) ──────────────────────────────────

/**
 * Interject into a running agent turn with input — answer its ask_user question,
 * or (as raw text) send a steering interjection or a run-control command
 * (`/pause`, `/resume`, `/report`). The backend classifies the text; the
 * assistant_message_ext_id identifies which streaming turn to interject into.
 */
export async function respondToAgent(
  arbi: ArbiClient,
  assistantMessageExtId: string,
  answer: string
) {
  return requireData(
    await arbi.fetch.POST('/v1/assistant/interject', {
      body: { assistant_message_ext_id: assistantMessageExtId, answer },
    }),
    'Failed to interject into agent'
  )
}

// ── Skills listing ──────────────────────────────────────────────────────────

/** A single user-invocable skill as exposed by ``GET /v1/assistant/skills``.
 *  Re-exported with the schema-generated shape so callers don't have to
 *  reach into ``components['schemas']`` themselves. */
export type SkillSummary = components['schemas']['SkillSummaryResponse']

/**
 * List the skills the caller can invoke in the active workspace.
 *
 * Powers slash-command autocomplete in the TUI, CLI, and React UI.
 * Returns lightweight metadata only (name, description, args hint) —
 * never the SKILL.md body. To read the body, fetch the document via
 * ``getParsedContent(authHeaders, doc_ext_id, 'content')``.
 *
 * Scoped to the workspace the caller has open (the backend uses the
 * session's ``active_workspace``); cross-workspace listing isn't
 * supported because each workspace has its own encryption key.
 *
 * @param includeHidden  When ``true``, returns skills with
 *   ``user-invocable: false`` in their frontmatter. Off by default —
 *   those are typically agent-only or in-progress skills that
 *   shouldn't surface in slash menus.
 */
export async function listSkills(
  arbi: ArbiClient,
  options?: { includeHidden?: boolean }
): Promise<SkillSummary[]> {
  const result = requireData(
    await arbi.fetch.GET('/v1/assistant/skills', {
      params: { query: { include_hidden: options?.includeHidden ?? false } },
    }),
    'Failed to list skills'
  )
  return result.skills
}

/** A built-in slash command as exposed by ``GET /v1/assistant/commands``.
 *  These are shipped commands (``/image``, ``/compact``, …) from the backend
 *  command registry — distinct from dynamic user-authored skills. */
export type CommandDescriptor = components['schemas']['CommandDescriptorResponse']

/**
 * List the built-in slash commands the caller can invoke (``/image``,
 * ``/compact``, ``/goal``, ``/pa``, …), respecting per-user gates
 * (``show_pa_mode`` / ``GOALS_ENABLED``).
 *
 * These come from the backend command registry — the single source of truth
 * shared with the dispatcher — so clients no longer hardcode the list. Merge
 * with {@link listSkills} to build a complete slash menu (built-ins + dynamic
 * user-authored skills).
 */
export async function listCommands(arbi: ArbiClient): Promise<CommandDescriptor[]> {
  const result = requireData(
    await arbi.fetch.GET('/v1/assistant/commands', {}),
    'Failed to list commands'
  )
  return result.commands
}

// ── Slash-command parsing (canonical) ──────────────────────────────────────
//
// Single source of truth for "what is a slash command" across the TUI
// (command-registry dispatcher + pre-flight hint), the CLI, and the
// React message-input autocomplete. Three frontends were each carrying
// a slightly different regex; consolidating here means a change to the
// allowed character set or the leading-whitespace rule lands in exactly
// one place.

/** Parsed slash-command shape, regardless of frontend. */
export interface ParsedSlashCommand {
  /** The lowercased token immediately after ``/``. */
  slug: string
  /** Everything after the first whitespace, untrimmed. ``""`` when the
   *  user hasn't typed any arguments yet. */
  args: string
}

/**
 * Parse a *submitted* slash command (or anything that walks like one).
 *
 * Permissive about whitespace: leading whitespace is tolerated so
 * pasted snippets like ``  /summarize foo`` still parse. The slug
 * grammar matches the backend's ``SkillManager._slug`` output (alnum
 * + underscore + dash), which is also what the autocomplete menus
 * surface, so there's exactly one definition of "valid slug."
 *
 * Returns ``null`` if the input doesn't start with ``/<slug>``.
 *
 * Use this in **dispatchers** (TUI command-registry) and **pre-flight
 * hint** paths where you want to react to any in-progress or
 * submitted slash command. For the narrower "user is typing a slug
 * with no args yet" UX, use ``parseSlashTokenInProgress``.
 */
export function parseSlashCommand(input: string): ParsedSlashCommand | null {
  const m = /^\s*\/([a-zA-Z0-9_-]+)(?:\s+([\s\S]*))?$/.exec(input)
  if (!m) return null
  return { slug: m[1].toLowerCase(), args: m[2] ?? '' }
}

/**
 * Parse a slash-command **in progress** — the buffer is just
 * ``/<token>`` with no committed arguments yet (no space after the
 * slug). This is the trigger condition for the autocomplete menu:
 * the moment the user types a space, the slug is "committed" and the
 * menu should hide so further keystrokes form the arguments.
 *
 * Empty token is allowed (a bare ``/``) so the menu opens immediately
 * and lists everything — same UX as Slack/Discord.
 */
export function parseSlashTokenInProgress(buffer: string): string | null {
  const m = /^\s*\/([a-zA-Z0-9_-]*)$/.exec(buffer)
  return m ? m[1].toLowerCase() : null
}

/**
 * Filter a list of skill summaries by a typed query and sort by
 * relevance: prefix matches first, then substring matches, alphabetic
 * within each bucket. Pinned slugs (the user's favourites — surfaced
 * by the React library modal) float to the very top.
 *
 * Pure function — same logic used by the React popover, the TUI
 * autocomplete, and the CLI. Pulling it out of the React component
 * means a change to the matching rules lands in one place.
 */
export function filterSkills<T extends { slug?: string; name: string }>(
  items: ReadonlyArray<T>,
  query: string,
  pinned: ReadonlyArray<string> = []
): T[] {
  const q = query.toLowerCase()
  const pinnedSet = new Set(pinned)
  // The slug-or-name fallback lets the same helper power React (where
  // the derived ``SkillSummary`` has only ``name``) and the SDK list
  // (``SkillSummary`` has both — ``slug`` is canonical).
  const key = (s: T) => (s.slug ?? s.name).toLowerCase()
  const matched = q ? items.filter((s) => key(s).includes(q)) : items.slice()
  matched.sort((a, b) => {
    const aPinned = pinnedSet.has(key(a)) ? 0 : 1
    const bPinned = pinnedSet.has(key(b)) ? 0 : 1
    if (aPinned !== bPinned) return aPinned - bPinned
    const aPrefix = key(a).startsWith(q) ? 0 : 1
    const bPrefix = key(b).startsWith(q) ? 0 : 1
    if (aPrefix !== bPrefix) return aPrefix - bPrefix
    return key(a).localeCompare(key(b))
  })
  return matched
}
